import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { computeTextDiff } from '../utils/documentDiff';
import './PendingChangesIndicator.css';

/**
 * Component to visualize pending changes in predictive state
 * Shows what changes are being applied and their status with diff highlighting
 */
const PendingChangesIndicator = ({
  pendingChanges = [],
  onConfirm,
  onReject,
  streamedContent = '',
  isStreaming = false,
  onToggleDiff,
  isDiffVisible = false,
}) => {
  if (pendingChanges.length === 0 && !isStreaming) {
    return null;
  }

  return (
    <div className="pending-changes-indicator">
      {isStreaming && (
        <div className="streaming-preview">
          <div className="streaming-header">
            <AccessTimeIcon fontSize="small" />
            <span>AI is writing...</span>
            <div className="streaming-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
          {streamedContent && (
            <div className="streamed-content-preview">
              <div className="preview-label">Live preview:</div>
              <div className="preview-text">
                {streamedContent.slice(0, 150)}
                {streamedContent.length > 150 ? '...' : ''}
              </div>
            </div>
          )}
        </div>
      )}

      {pendingChanges.length > 0 && (
        <div className="pending-changes-list">
          <div className="changes-header">
            <span className="changes-count">
              {pendingChanges.length} change{pendingChanges.length > 1 ? 's' : ''}
            </span>
          </div>
          {pendingChanges.map((change) => {
            return (
            <div
              key={change.id}
              className={`pending-change-item ${change.status}`}
            >
              <div className="change-info">
                <Chip
                  label={change.type === 'ai_edit' ? 'AI Edit' : change.type}
                  size="small"
                  color={change.status === 'confirmed' ? 'success' : 'default'}
                  className="change-type-chip"
                />
                {change.instruction && (
                  <span className="change-instruction">
                    {change.instruction.slice(0, 50)}
                    {change.instruction.length > 50 ? '...' : ''}
                  </span>
                )}
                {change.timestamp && (
                  <span className="change-timestamp">
                    {new Date(change.timestamp).toLocaleTimeString()}
                  </span>
                )}
                {/* Toggle inline diff view */}
                <IconButton
                  size="small"
                  onClick={onToggleDiff}
                  className="expand-button"
                  title={isDiffVisible ? "Hide changes" : "Show changes in editor"}
                >
                  {isDiffVisible ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
              </div>

              {change.status === 'pending' && (
                <div className="change-actions">
                  <IconButton
                    size="small"
                    onClick={() => onConfirm && onConfirm(change.id)}
                    className="confirm-button"
                    title="Accept change"
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => onReject && onReject(change.id)}
                    className="reject-button"
                    title="Reject change"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </div>
              )}

              {change.status === 'confirmed' && (
                <Chip
                  icon={<CheckIcon />}
                  label="Applied"
                  size="small"
                  color="success"
                  className="status-chip"
                />
              )}
            </div>
          )})}
        </div>
      )}
    </div>
  );
};

PendingChangesIndicator.propTypes = {
  pendingChanges: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      type: PropTypes.string,
      content: PropTypes.string,
      instruction: PropTypes.string,
      timestamp: PropTypes.string,
      status: PropTypes.oneOf(['pending', 'confirmed', 'rejected']),
    })
  ),
  onConfirm: PropTypes.func,
  onReject: PropTypes.func,
  streamedContent: PropTypes.string,
  isStreaming: PropTypes.bool,
};

export default PendingChangesIndicator;

